import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import api from '../services/api';

/**
 * useFilterOptions Hook
 * Custom hook for managing filter options state and API calls.
 * 
 * Task: P2-006 (Updated for Equal-Priority Cascading Filters)
 * 
 * Features:
 * - Debounced API calls (300ms)
 * - Request cancellation with AbortController
 * - Error handling
 * - Loading states
 * - Total counts for live result counter
 */
export const useFilterOptions = (selectedFilters) => {
  const [mainGroups, setMainGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [subGroupCounts, setSubGroupCounts] = useState({});
  const [totalMatchingParts, setTotalMatchingParts] = useState(0);
  const [totalAllParts, setTotalAllParts] = useState(0);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  
  const abortControllerRef = useRef(null);

  // Load filter options from API
  const loadFilterOptions = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      
      if (selectedFilters.mainGroups?.length > 0) {
        params.append('main_group', selectedFilters.mainGroups.join(','));
      }
      if (selectedFilters.subGroups?.length > 0) {
        params.append('subgroup', selectedFilters.subGroups.join(','));
      }
      if (selectedFilters.manufacturers?.length > 0) {
        params.append('manufacturer', selectedFilters.manufacturers.join(','));
      }
      if (selectedFilters.status) {
        params.append('status', selectedFilters.status);
      }
      
      const response = await api.get(`/api/v1/parts/filter-options?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });
      
      const data = response.data;
      setMainGroups(data.available_main_groups || []);
      setSubGroups(data.available_subgroups || []);
      setManufacturers(data.available_manufacturers || []);
      setStatuses(data.available_statuses || []);
      setTotalMatchingParts(data.total_matching_parts || 0);
      setTotalAllParts(data.total_all_parts || 0);
      setIsInitialLoad(false);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Failed to load filter options:', err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedFilters.mainGroups, selectedFilters.subGroups, selectedFilters.manufacturers, selectedFilters.status]);

  // Debounced version
  const debouncedLoad = useDebouncedCallback(loadFilterOptions, 300);

  // Load subgroup counts (single API call)
  const loadSubGroupCounts = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/subgroup-counts');
      setSubGroupCounts(response.data || {});
    } catch (err) {
      console.error('Failed to load subgroup counts:', err);
    }
  }, []);

  // Effect for filter options - depends on selectedFilters to trigger re-fetch
  useEffect(() => {
    debouncedLoad();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedLoad, selectedFilters.mainGroups, selectedFilters.subGroups, selectedFilters.manufacturers, selectedFilters.status]);

  // Effect for subgroup counts (once on mount)
  useEffect(() => {
    loadSubGroupCounts();
  }, [loadSubGroupCounts]);

  // Helpers
  const getStatusCount = (statusValue) => {
    const status = statuses.find(s => s.status === statusValue);
    return status?.count || 0;
  };

  const getSubGroupCountForMainGroup = (code) => {
    return subGroupCounts[code] || 0;
  };

  // Split items by relevance (count > 0 vs count = 0)
  // Also sort by count descending within each group
  const splitByRelevance = (items, selectedItems = []) => {
    const selected = [];
    const relevant = [];
    const zeroCount = [];
    
    items.forEach(item => {
      const itemKey = item.code || item.name || `${item.main_group_code}-${item.subgroup_code}` || item.status;
      const isSelected = selectedItems.includes(itemKey);
      
      if (isSelected) {
        selected.push(item);
      } else if ((item.count || 0) > 0) {
        relevant.push(item);
      } else {
        zeroCount.push(item);
      }
    });
    
    // Sort each group by count descending
    const sortByCount = (a, b) => (b.count || 0) - (a.count || 0);
    selected.sort(sortByCount);
    relevant.sort(sortByCount);
    zeroCount.sort(sortByCount);
    
    return { selected, relevant, zeroCount };
  };

  return {
    // Data
    mainGroups,
    subGroups,
    manufacturers,
    statuses,
    subGroupCounts,
    totalMatchingParts,
    totalAllParts,
    
    // State
    isLoading,
    isInitialLoad,
    error,
    
    // Helpers
    getStatusCount,
    getSubGroupCountForMainGroup,
    splitByRelevance,
    
    // Actions
    reload: loadFilterOptions
  };
};

export default useFilterOptions;
